import { useGSAP } from "@gsap/react"
import clsx from "clsx"
import gsap from "gsap"
import {
  ComponentPropsWithoutRef,
  FC,
  PropsWithChildren,
  memo,
  useEffect,
  useRef,
  useState,
  useCallback
} from "react"
import { getDistance } from "@/helpers/gsap/getElementDistance"
import styles from "./SwitchInline.module.scss"
import { SwitchInlineItem } from "./ui/SwitchInlineItem"

interface ISwitch extends ComponentPropsWithoutRef<"div"> {
  initSelect?: number
}

const SwitchComp: FC<PropsWithChildren<ISwitch>> = memo(
  ({ children, className, initSelect, ...props }) => {
    const [list, setList] = useState<Element[]>([])
    const [activeIndex, setActiveSelect] = useState<number>(-1)
    const [activeIsNew, setActiveIsNew] = useState<boolean>(false)

    const refContent = useRef<HTMLDivElement>(null)
    const refSelect = useRef<HTMLDivElement>(null)

    const positionSelect = useCallback(() => {
      if (!refContent.current || !refSelect.current) return
      const item = list[activeIndex] as HTMLDivElement | undefined
      if (!item) return

      const rect = item.getBoundingClientRect()
      const dist = getDistance(refContent.current, item, [0, 0], [0, 0])

      gsap.to(refSelect.current, {
        width: rect.width + (activeIndex === 0 ? 4 : 0),
        x: dist.x + (activeIndex === 0 ? -4 : 0),
        opacity: 1,
        duration: 0.3,
        ease: "power2"
      })
    }, [activeIndex, list])

    const debouncedPosition = useCallback(() => {
      gsap.killTweensOf(refSelect.current)
      gsap.delayedCall(0.02, positionSelect)
    }, [positionSelect])

    useEffect(() => {
      if (!refContent.current) return

      const select = 0
      const listEls = gsap.utils.toArray(
        ".switch-list-item",
        refContent.current
      ) as HTMLDivElement[]

      listEls[select]?.classList.add("active")
      setList(listEls)

      listEls.forEach((e, i) =>
        e.addEventListener("click", () => {
          setActiveSelect(i)
          listEls.forEach((el) => el.classList.remove("active"))
          e.classList.add("active")
          setActiveIsNew(e.classList.contains("new"))
        })
      )

      const t = setTimeout(() => setActiveSelect(select), 100)
      return () => clearTimeout(t)
    }, [])

    useEffect(() => {
      const t = setTimeout(() => {
        if (!list[0]) return
        const idx = initSelect ?? 0
        setActiveSelect(idx)
        list.forEach((e) => e.classList.remove("active"))
        list[idx].classList.add("active")
        setActiveIsNew(list[idx].classList.contains("new"))
      }, 100)
      return () => clearTimeout(t)
    }, [initSelect, list])

    useGSAP(() => {
      positionSelect()
    }, [positionSelect, activeIndex, list])

    useEffect(() => {
      const onResize = () => debouncedPosition()
      window.addEventListener("resize", onResize)

      const ro = new ResizeObserver(() => debouncedPosition())
      if (refContent.current) ro.observe(refContent.current)
      list.forEach((el) => ro.observe(el))

      return () => {
        window.removeEventListener("resize", onResize)
        ro.disconnect()
      }
    }, [debouncedPosition, list])

    return (
      <div className={clsx(styles.SwitchWrap, className)} {...props}>
        <div className={clsx(styles.Switch)} ref={refContent}>
          <div
            className={clsx(styles.Selector_select, activeIsNew && styles.new)}
            ref={refSelect}
          />
          {children}
        </div>
      </div>
    )
  }
)

SwitchComp.displayName = "SwitchComp"

export const SwitchInline = Object.assign(SwitchComp, {
  Item: SwitchInlineItem
})
